import { NgModule } from '@angular/core';
import { ActivatedRouteSnapshot, Route, RouterModule, Routes } from '@angular/router';

// Re-run the resolver only when query params changed (filters, page, search),
// not when only the path changes (e.g. /services -> /services/42).
function rerunOnQueryParamsChange(from: ActivatedRouteSnapshot, to: ActivatedRouteSnapshot): boolean {
  return JSON.stringify(from.queryParams) !== JSON.stringify(to.queryParams);
}

function rerunOnParamsOrQueryParamsChangeExceptNewTicket(from: ActivatedRouteSnapshot, to: ActivatedRouteSnapshot): boolean {
  return (
    JSON.stringify(from.params) !== JSON.stringify(to.params) ||
    JSON.stringify(withoutNewTicketQueryParam(from.queryParams)) !== JSON.stringify(withoutNewTicketQueryParam(to.queryParams))
  );
}
import { AuthGuardService as AuthGuard } from './services';
import {
  SignInComponent,
  UsersComponent,
  OperatorsComponent,
  VehiclesComponent,
  TownsComponent,
  ServicesComponent,
  BookingsComponent,
  RouteReorganizationComponent,
  SetupComponent,
  AvailabilitiesComponent,
} from './pages';
import { UserRole } from './models/enums/userRole';
import { TripsViewMode } from './models/enums/tripsViewMode';
import { TripsComponent } from './pages/trips/trips.component';
import { AvailabilitiesResolver, BookingsResolver, CommunicationsResolver, ServiceInfosResolver, SetupResolver, TripsResolver, UsersResolver } from './resolvers';
import { bookingsHydrationGuard } from './guards/bookings-hydrate.guard';
import { usersHydrationGuard } from './guards/users-hydrate.guard';
import { servicesHydrationGuard } from './guards/services-hydrate.guard';
import { tripsHydrationGuard } from './guards/trips-hydrate.guard';
import { communicationsHydrationGuard } from './guards/communications-hydrate.guard';
import { availabilitiesHydrationGuard } from './guards/availabilities-hydrate.guard';
import { vehiclesHydrationGuard } from './guards/vehicles-hydrate.guard';
import { unsavedChangesGuard } from './guards/unsaved-changes.guard';
import { signInRedirectGuard } from './guards/sign-in-redirect.guard';
import { pushAlertGuard } from './guards/push-alert.guard';
import { CommunicationsComponent } from './pages/communications/communications.component';
import { withoutNewTicketQueryParam } from './utils/detailTicketDrawer';
import { roleGuard } from './guards/role.guard';
import { IntegrationsTab } from './models/enums';
import { APP } from './config/app';

function withManagementDetailRoute(route: Route): Routes {
  return [route, { ...route, path: `${route.path}/:${APP.MANAGEMENT_DETAIL_ID_PARAM}` }];
}

const routes: Routes = [
  { path: '', component: SignInComponent, canActivate: [signInRedirectGuard] },
  ...withManagementDetailRoute({
    path: 'clients',
    loadComponent: () => import('./pages/clients/clients.component').then((module) => module.ClientsComponent),
    canActivate: [AuthGuard, roleGuard],
    data: {
      roles: [UserRole.SUPERADMIN],
    },
  }),
  {
    path: 'integrations',
    loadComponent: () =>
      import('./pages/integrations/integrations.component').then((module) => module.IntegrationsComponent),
    canActivate: [AuthGuard, roleGuard],
    data: {
      roles: [UserRole.SUPERADMIN],
    },
    children: [
      { path: '', pathMatch: 'full', redirectTo: IntegrationsTab.APPS },
      ...withManagementDetailRoute({
        path: IntegrationsTab.APPS,
        loadComponent: () => import('./pages/apps/apps.component').then((module) => module.AppsComponent),
      }),
      ...withManagementDetailRoute({
        path: IntegrationsTab.IDENTITY_PROVIDERS,
        loadComponent: () =>
          import('./pages/identity-provider/identity-provider.component').then(
            (module) => module.IdentityProviderComponent,
          ),
      }),
      ...withManagementDetailRoute({
        path: IntegrationsTab.SAE_INTEGRATIONS,
        loadComponent: () =>
          import('./pages/sae-integrations/sae-integrations.component').then(
            (module) => module.SaeIntegrationsComponent,
          ),
      }),
    ],
  },
  {
    path: 'users',
    component: UsersComponent,
    canActivate: [AuthGuard, usersHydrationGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
      ],
    },
    resolve: { users: UsersResolver },
  },
  {
    path: 'drivers',
    component: UsersComponent,
    canActivate: [AuthGuard, usersHydrationGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { users: UsersResolver },
  },
  {
    path: 'suggestions/users',
    component: CommunicationsComponent,
    canActivate: [AuthGuard, communicationsHydrationGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
      ],
    },
    resolve: { suggestions: CommunicationsResolver },
  },
  {
    path: 'suggestions/users/:id',
    component: CommunicationsComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
      ],
    },
    resolve: { suggestions: CommunicationsResolver },
  },
  {
    path: 'suggestions/drivers',
    component: CommunicationsComponent,
    canActivate: [AuthGuard, communicationsHydrationGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { suggestions: CommunicationsResolver },
  },
  {
    path: 'suggestions/drivers/:id',
    component: CommunicationsComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { suggestions: CommunicationsResolver },
  },
  {
    path: 'messages/users',
    component: CommunicationsComponent,
    canActivate: [AuthGuard, communicationsHydrationGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { messages: CommunicationsResolver },
  },
  {
    path: 'messages/users/:id',
    component: CommunicationsComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { messages: CommunicationsResolver },
  },
  {
    path: 'messages/drivers',
    component: CommunicationsComponent,
    canActivate: [AuthGuard, communicationsHydrationGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { messages: CommunicationsResolver },
  },
  {
    path: 'messages/drivers/:id',
    component: CommunicationsComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { messages: CommunicationsResolver },
  },
  {
    path: 'push-alert',
    canActivate: [AuthGuard, pushAlertGuard],
    children: [],
  },
  {
    path: 'messages/admins',
    redirectTo: 'messages/users',
    pathMatch: 'full',
  },
  {
    path: 'messages/admins/:id',
    redirectTo: 'messages/users/:id',
  },
  {
    path: 'users/:id',
    component: UsersComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: rerunOnParamsOrQueryParamsChangeExceptNewTicket,
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
      ],
    },
    resolve: { users: UsersResolver },
  },
  {
    path: 'drivers/:id',
    component: UsersComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: rerunOnParamsOrQueryParamsChangeExceptNewTicket,
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { users: UsersResolver },
  },
  {
    path: 'operators',
    component: OperatorsComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.SUPERADMIN],
    },
  },
  {
    path: 'vehicles',
    component: VehiclesComponent,
    canActivate: [AuthGuard, vehiclesHydrationGuard],
    canDeactivate: [unsavedChangesGuard],
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.DRIVER_MANAGER,
      ],
    },
  },
  {
    path: 'vehicles/:id',
    component: VehiclesComponent,
    canActivate: [AuthGuard, vehiclesHydrationGuard],
    canDeactivate: [unsavedChangesGuard],
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.DRIVER_MANAGER,
      ],
    },
  },
  {
    path: 'towns',
    component: TownsComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
  },
  {
    path: 'services',
    component: ServicesComponent,
    canActivate: [AuthGuard, servicesHydrationGuard],
    canDeactivate: [unsavedChangesGuard],
    runGuardsAndResolvers: rerunOnQueryParamsChange,
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { services: ServiceInfosResolver },
  },
  {
    path: 'services/new/setup',
    component: SetupComponent,
    canActivate: [ AuthGuard ],
    data: {
      new: true,
      wizard: true,
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { setups: SetupResolver },
  },
  {
    path: 'services/new/setup/backwards',
    component: SetupComponent,
    canActivate: [ AuthGuard ],
    data: {
      new: true,
      wizard: true,
      backwards: true,
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { setups: SetupResolver },
  },
  {
    path: 'services/:id',
    component: ServicesComponent,
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard],
    runGuardsAndResolvers: rerunOnQueryParamsChange,
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { services: ServiceInfosResolver },
  },
  {
    path: 'services/:id/reorganize',
    component: RouteReorganizationComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
  },
  {
    path: 'services/:id/reorganize/summary',
    component: RouteReorganizationComponent,
    canActivate: [AuthGuard],
    data: {
      summary: true,
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
  },
  {
    path: 'services/:id/reorganize/setup/:routeNumber',
    component: SetupComponent,
    canActivate: [AuthGuard],
    data: {
      new: true,
      wizard: true,
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { setups: SetupResolver },
  },
  {
    path: 'services/:id/reorganize/route/:routeNumber',
    component: ServicesComponent,
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard],
    runGuardsAndResolvers: rerunOnQueryParamsChange,
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { services: ServiceInfosResolver },
  },
  {
    path: 'services/:id/:subRoute',
    component: ServicesComponent,
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard],
    runGuardsAndResolvers: rerunOnQueryParamsChange,
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { services: ServiceInfosResolver },
  },
  {
    path: 'services/:id/:subRoute/:routeId',
    component: ServicesComponent,
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard],
    runGuardsAndResolvers: rerunOnQueryParamsChange,
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { services: ServiceInfosResolver },
  },
  {
    path: 'services/:id/:subRoute/:routeId/design',
    component: ServicesComponent,
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard],
    runGuardsAndResolvers: rerunOnQueryParamsChange,
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { services: ServiceInfosResolver },
  },
  {
    path: 'services/:id/routes/:routeId/setups',
    component: SetupComponent,
    canActivate: [ AuthGuard ],
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { setups: SetupResolver },
  },
  {
    path: 'services/:id/routes/:routeId/setups/:setupId',
    component: SetupComponent,
    canActivate: [ AuthGuard ],
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { setups: SetupResolver },
  },
  {
    path: 'services/:id/routes/:routeId/setups/:setupId/availabilities',
    component: SetupComponent,
    canActivate: [ AuthGuard ],
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { setups: SetupResolver },
  },
  {
    path: 'services/:id/routes/:routeId/availabilities',
    component: AvailabilitiesComponent,
    canActivate: [ AuthGuard, availabilitiesHydrationGuard ],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { availabilities: AvailabilitiesResolver },
  },
  {
    path: 'services/:id/routes/:routeId/itineraries',
    loadComponent: () =>
      import('./pages/itineraries/itineraries.component').then((module) => module.ItinerariesComponent),
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
  },
  {
    path: 'services/:id/routes/:routeId/itineraries/:itineraryId',
    loadComponent: () =>
      import('./pages/itineraries/itineraries.component').then((module) => module.ItinerariesComponent),
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
  },
  {
    path: 'services/:id/:subRoute/:routeId/alterations',
    component: ServicesComponent,
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard],
    runGuardsAndResolvers: rerunOnQueryParamsChange,
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { services: ServiceInfosResolver },
  },
  {
    path: 'services/:id/:subRoute/:routeId/restrictions',
    component: ServicesComponent,
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard],
    runGuardsAndResolvers: rerunOnQueryParamsChange,
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { services: ServiceInfosResolver },
  },
  {
    path: 'trips',
    component: TripsComponent,
    canActivate: [AuthGuard, tripsHydrationGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { trips: TripsResolver },
    children: [
      {
        path: 'positions',
        component: TripsComponent,
        data: { viewMode: TripsViewMode.POSITIONS },
      },
      {
        path: 'positions/bookings/:id',
        component: TripsComponent,
        data: { viewMode: TripsViewMode.POSITIONS, bookingsOnly: true },
      },
      {
        path: 'positions/:id/bookings',
        component: TripsComponent,
        data: { viewMode: TripsViewMode.POSITIONS },
      },
      {
        path: 'positions/:id',
        component: TripsComponent,
        data: { viewMode: TripsViewMode.POSITIONS },
      },
      {
        path: 'bookings/:id',
        component: TripsComponent,
        data: { bookingsOnly: true },
      },
      {
        path: ':id/bookings',
        component: TripsComponent,
      },
      {
        path: ':id',
        component: TripsComponent,
      },
    ],
  },
  {
    path: 'bookings',
    component: BookingsComponent,
    canActivate: [AuthGuard, bookingsHydrationGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { bookings: BookingsResolver },
  },
  {
    path: 'bookings/new',
    component: BookingsComponent,
    canActivate: [AuthGuard],
    data: {
      new: true,
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { bookings: BookingsResolver },
  },
  {
    path: 'bookings/users/:userId',
    component: BookingsComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { bookings: BookingsResolver },
  },
  {
    path: 'bookings/users/:userId/:id',
    component: BookingsComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { bookings: BookingsResolver },
  },
  {
    path: 'bookings/:id',
    component: BookingsComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: rerunOnParamsOrQueryParamsChangeExceptNewTicket,
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { bookings: BookingsResolver },
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload' })],
  exports: [RouterModule],
})
export class AppRoutingModule { }
